import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Users, BarChart3, Zap, Shield, Target, Sparkles, ArrowRight, Star, Crown, Swords } from 'lucide-react';

const HomePage = () => {
  const [mexicoTime, setMexicoTime] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setMexicoTime(now.toLocaleTimeString('es-MX', {
        timeZone: 'America/Mexico_City',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: Upload,
      title: 'Extracción Automática',
      description: 'Sube una captura de pantalla y extrae todos los datos del perfil automáticamente con IA',
      color: 'from-orange-500 to-yellow-500',
      borderColor: 'border-orange-400'
    },
    {
      icon: Users,
      title: 'Gestión de Perfiles',
      description: 'Guarda y organiza todos los perfiles extraídos en tu base de datos personal',
      color: 'from-cyan-500 to-blue-500',
      borderColor: 'border-cyan-400'
    },
    {
      icon: BarChart3,
      title: 'Comparación de Stats',
      description: 'Compara múltiples perfiles lado a lado para analizar fortalezas y debilidades',
      color: 'from-green-500 to-emerald-500',
      borderColor: 'border-green-400'
    }
  ];

  const stats = [
    { value: '99%', label: 'Precisión', icon: Target },
    { value: '<5s', label: 'Velocidad', icon: Zap },
    { value: '∞', label: 'Perfiles', icon: Users }
  ];

  return (
    <div className="space-y-16">
      {/* Hero Section */}
      <section className="text-center py-12 relative">
        {/* Decorative elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-full">
          <div className="absolute top-10 left-10 animate-float" style={{animationDelay: '0s'}}>
            <Star className="w-8 h-8 text-yellow-400/30" />
          </div>
          <div className="absolute top-20 right-20 animate-float" style={{animationDelay: '1s'}}>
            <Crown className="w-10 h-10 text-orange-400/30" />
          </div>
          <div className="absolute bottom-20 left-20 animate-float" style={{animationDelay: '2s'}}>
            <Swords className="w-8 h-8 text-cyan-400/30" />
          </div>
        </div>

        <div className="relative inline-block mb-8">
          {/* Glow effect behind title */}
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/30 to-yellow-500/30 blur-3xl scale-150"></div>
          
          <div className="relative">
            {/* Stars */}
            <div className="flex items-center justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8 text-yellow-400 fill-yellow-400 drop-shadow-lg" style={{filter: 'drop-shadow(0 0 10px rgba(250, 204, 21, 0.5))'}} />
              ))}
            </div>
            
            {/* Main Title */}
            <h1 className="text-6xl md:text-8xl font-black game-title mb-4 tracking-wider">
              LAST WAR
            </h1>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-3" style={{textShadow: '0 0 20px rgba(255,255,255,0.3)'}}>
              Profile Extractor
            </h2>
            <p className="game-subtitle text-xl tracking-[0.5em]">
              SISTEMA DE ANÁLISIS
            </p>
          </div>
        </div>

        <p className="text-cyan-100/90 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
          Extrae automáticamente toda la información de perfiles de Last War 
          usando <span className="text-orange-400 font-bold">Inteligencia Artificial</span>. 
          Solo sube una captura de pantalla y obtén los datos al instante.
        </p>

        {/* CTA Buttons - Game Style */}
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
          <Link to="/extract">
            <button data-testid="extract-profile-btn" className="game-btn flex items-center gap-3 text-xl px-10 py-4">
              <Upload className="w-6 h-6" />
              EXTRAER PERFIL
              <ArrowRight className="w-6 h-6" />
            </button>
          </Link>
          <Link to="/profiles">
            <button data-testid="view-profiles-btn" className="game-btn game-btn-blue flex items-center gap-3 text-xl px-10 py-4">
              <Users className="w-6 h-6" />
              VER PERFILES
            </button>
          </Link>
        </div>
      </section>

      {/* Stats Section - Game Cards */}
      <section className="grid grid-cols-3 gap-4 max-w-3xl mx-auto">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="game-card game-card-gold p-6 text-center hover:scale-105 transition-transform duration-300">
              <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-orange-500/30 to-yellow-500/30 rounded-xl flex items-center justify-center border border-orange-400/50">
                <Icon className="w-7 h-7 text-orange-400" />
              </div>
              <p className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400" style={{textShadow: '0 0 20px rgba(255, 200, 0, 0.3)'}}>
                {stat.value}
              </p>
              <p className="text-cyan-300 text-sm font-bold mt-1">{stat.label}</p>
            </div>
          );
        })}
      </section>

      {/* Features Section */}
      <section>
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4" style={{textShadow: '0 0 20px rgba(255,255,255,0.2)'}}>
            Características <span className="text-orange-400">Principales</span>
          </h2>
          <p className="text-cyan-300/80 max-w-xl mx-auto">
            Todo lo que necesitas para analizar y gestionar perfiles de Last War
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className={`game-card p-6 hover:scale-105 transition-all duration-500 group ${feature.borderColor}`}>
                <div className={`w-20 h-20 mb-5 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 border-2 border-white/30`}>
                  <Icon className="w-10 h-10 text-white drop-shadow-lg" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-orange-400 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-cyan-200/80 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works - Game Style */}
      <section className="game-card p-8 md:p-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            ¿Cómo <span className="text-cyan-400">Funciona</span>?
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            { step: '1', title: 'Captura', desc: 'Toma una captura de pantalla del perfil en Last War', icon: '📸', color: 'from-orange-500 to-yellow-500' },
            { step: '2', title: 'Sube', desc: 'Arrastra o selecciona la imagen en nuestro extractor', icon: '⬆️', color: 'from-cyan-500 to-blue-500' },
            { step: '3', title: 'Listo', desc: 'La IA extrae y muestra todos los datos automáticamente', icon: '✨', color: 'from-green-500 to-emerald-500' }
          ].map((item, index) => (
            <div key={index} className="text-center group">
              <div className="relative inline-block mb-6">
                <div className={`w-24 h-24 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center text-5xl shadow-xl group-hover:scale-110 transition-transform duration-300 border-3 border-white/30`}>
                  {item.icon}
                </div>
                <div className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center text-white font-black text-xl border-2 border-white shadow-lg">
                  {item.step}
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">{item.title}</h3>
              <p className="text-cyan-200/80">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="text-center py-12">
        <div className="relative inline-block">
          <Sparkles className="absolute -top-8 -left-8 w-10 h-10 text-yellow-400 animate-pulse" />
          <Sparkles className="absolute -bottom-8 -right-8 w-10 h-10 text-cyan-400 animate-pulse" style={{animationDelay: '0.5s'}} />
          
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
            ¿Listo para <span className="text-orange-400">empezar</span>?
          </h2>
          <p className="text-cyan-300/80 mb-10 max-w-md mx-auto text-lg">
            Extrae tu primer perfil ahora y descubre el poder del análisis automático
          </p>
          
          <Link to="/extract">
            <button data-testid="start-now-btn" className="game-btn text-2xl px-14 py-5 flex items-center gap-4 mx-auto">
              <Shield className="w-8 h-8" />
              COMENZAR AHORA
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
